"use client";

import { AppTooltip } from "@/common";
import { DataConstant } from "@/const";
import { ArrowIcon } from "@/icons";
import { useAppSelector } from "@/redux-store";
import { Box, Fade } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import clsx from "clsx";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import HeaderLogo from "../Header/HeaderLogo";
import SidebarCollapse from "./SidebarCollapse";
import SidebarTooltip from "./SidebarTooltip";
import { IMenuItemModel, ISession } from "@/models";
import { PermissionKeyEnum } from "@/const/data.const";
export const filterMenuByPermissions = (
  menuItems: IMenuItemModel[],
  allowedPermissions: PermissionKeyEnum[],
): IMenuItemModel[] => {
  return menuItems
    .filter((item) => {
      // Nếu item có permission, kiểm tra xem permission có nằm trong allowedPermissions không
      if (item.permission) {
        return allowedPermissions.includes(item.permission);
      }
      // Nếu item không có permission thì vẫn giữ lại
      return true;
    })
    .map((item) => {
      // Nếu item có children, lọc children với hàm đệ quy
      if (item.children && item.children.length > 0) {
        const filteredChildren = filterMenuByPermissions(
          item.children,
          allowedPermissions,
        );
        return { ...item, children: filteredChildren };
      }
      return item;
    });
};
const Sidebar = ({ session }: { session: ISession | null }) => {
  const path = usePathname();
  const router = useRouter();
  const listMenuAccess = useMemo(() => {
    if (session?.user.permissionKeys.includes(PermissionKeyEnum.SupperAdmin)) {
      return DataConstant.LIST_MENU_SIDEBAR;
    }
    return filterMenuByPermissions(
      DataConstant.LIST_MENU_SIDEBAR,
      session?.user.permissionKeys || [],
    );
  }, [session]);

  const listMenuByPath = useMemo(() => {
    const list = listMenuAccess.map((c) => {
      const item = { ...c, isActive: false };
      if (item.url === path) {
        item.isActive = true;
      }
      item.children?.forEach((c) => (c.isActive = false));
      const childIndex = item.children?.findIndex((e) => e.url === path);
      if (childIndex !== undefined && childIndex > -1 && item.children) {
        item.children[childIndex].isActive = true;
        item.isActive = true;
        item.isCollapse = true;
      }
      return item;
    });
    return list;
  }, [path, listMenuAccess]);

  const [listMenu, setListMenu] = useState(listMenuByPath);
  const [moveTooltip, setMoveTooltip] = useState<null | string>(null);
  const [isReverse, setIsReverse] = useState(false);
  const isMobile = useMediaQuery("(max-width: 991.98px)");

  const isShowFullMenu = useAppSelector(
    (state) => state.commonReducer.isShowFullMenu,
  );

  const handleClickToogleMenu = (index: number) => {
    if (listMenu[index].url) router.push(listMenu[index].url);
    else {
      listMenu[index].isCollapse = !listMenu[index].isCollapse;
      listMenu.forEach((c, i) => {
        if (i !== index) {
          c.isCollapse = false;
        }
      });
      setListMenu([...listMenu]);
    }
  };

  useEffect(() => {
    setListMenu(listMenuByPath);
  }, [listMenuByPath]);

  const handleMoveInTooltip = useCallback(
    (name: string) => {
      if (!moveTooltip) {
        setMoveTooltip(name);
      }
    },
    [moveTooltip],
  );

  const handleLeaveOutTooltip = useCallback(() => {
    setMoveTooltip(null);
  }, []);

  return (
    <Box
      className={clsx("main-left flex flex-col", {
        "w-[270px] translate-x-0": isMobile && isShowFullMenu,
        "w-[270px] -translate-x-full": isMobile && !isShowFullMenu,
        "w-[270px]": !isMobile && isShowFullMenu,
        "w-[72px]": !isMobile && !isShowFullMenu,
      })}
    >
      <HeaderLogo isShowFullMenu={isShowFullMenu} />
      <Box
        component={"ul"}
        className="p-0 list-none m-0 h-full flex-auto overflow-auto no-scrollbar"
      >
        {listMenu.map((item, index) => {
          const isMoveInTooltip = item.name === moveTooltip;

          return (
            <AppTooltip
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: "bg.bg1",
                    color: "common.white",
                    borderRadius: 0,
                    boxShadow: "none",
                    minWidth: 161,
                    transition: "all 0.3s linear",
                    position: "relative",
                    padding: 0,
                    top: isReverse ? 2 : -2,
                    left: -14,
                  },
                },
              }}
              placement="right-start"
              key={index}
              PopperProps={{
                modifiers: [
                  {
                    name: "updateState",
                    enabled: true,
                    phase: "afterWrite",
                    fn: ({ state }) => {
                      setIsReverse(state.placement === "right-end");
                    },
                  },
                ],
              }}
              title={
                !isShowFullMenu && (
                  <SidebarTooltip
                    onMouseEnter={() =>
                      handleMoveInTooltip(item.name as string)
                    }
                    onMouseLeave={handleLeaveOutTooltip}
                    item={item}
                    isReverse={isReverse}
                  />
                )
              }
            >
              <Box
                component={"li"}
                key={index}
                className="text-disabled-text relative"
              >
                <Link
                  href={item.url}
                  className={clsx(
                    "duration-300 flex items-center w-full gap-2 px-3 py-[14px] flex-nowrap hover:bg-default-hover text-white hover:text-white text-ellipsis overflow-hidden text-nowrap",
                    {
                      "bg-default-hover text-white": item.isActive,
                      "pr-9": isShowFullMenu,
                      "pl-6": !isShowFullMenu,
                      "bg-default-hover": isMoveInTooltip,
                      "text-white": isMoveInTooltip,
                    },
                  )}
                  onClick={(event) => {
                    event.preventDefault();
                    handleClickToogleMenu(index);
                  }}
                >
                  <Box
                    className="w-6 h-6 inline-flex items-center text-qi-h2"
                    component={"span"}
                  >
                    {item.icon}
                  </Box>
                  {isShowFullMenu ? (
                    <Box
                      component={"span"}
                      className="w-full overflow-hidden text-ellipsis"
                    >
                      {item.name}
                    </Box>
                  ) : (
                    ""
                  )}
                  {isShowFullMenu &&
                    item.children &&
                    !!item.children.length && (
                      <Box
                        className={clsx(
                          "absolute right-3 top-[16px] transition-all w-5 h-5 min-w-0 duration-300 ease-linear p-1 h5 flex items-center",
                          {
                            "-rotate-90 text-white": item.isCollapse,
                            "-rotate-180": !item.isCollapse,
                          },
                        )}
                      >
                        <ArrowIcon />
                      </Box>
                    )}
                </Link>
                <SidebarCollapse item={item} isShowFullMenu={isShowFullMenu} />
              </Box>
            </AppTooltip>
          );
        })}
      </Box>
    </Box>
  );
};

export default memo(Sidebar);
